import Plot from "react-plotly.js";
import React, {useEffect, useRef, useState} from 'react';
import {VEHICLE_DIMENSIONS_KEY_ENUM} from "./constants";
import {getMaxFinite, getMinFinite} from "../../../../helpers/graphs";

const Plotly2DTracesTop = (props) => {
  Math.radians = (degrees) => degrees * Math.PI / 180;

  const length = 400;

  const [radarX, setRadarX] = useState([]);
  const [radarY, setRadarY] = useState([]);
  const [radarA, setRadarA] = useState([]);

  const [camerax, setCameraX] = useState([]);
  const [cameray, setCameraY] = useState([]);
  const [cameraa, setCameraA] = useState([]);

  const [imux, setIMUX] = useState([]);
  const [imuy, setIMUY] = useState([]);
  const [imua, setIMUA] = useState([]);

  const [antennax, setAntennaX] = useState([]);
  const [antennay, setAntennaY] = useState([]);

  const [lidarX, setLidarX] = useState([]);
  const [lidarY, setLidarY] = useState([]);
  const [lidarA, setLidarA] = useState([]);

  const [ultrasonicsX, setUltrasonicsX] = useState([]);
  const [ultrasonicsY, setUltrasonicsY] = useState([]);
  const [ultrasonicsA, setUltrasonicsA] = useState([]);

  const [vehicle_height, setVehicleHeight] = useState(-1);
  const [vehicle_length, setVehicleLength] = useState(-1);
  const [vehicle_width, setVehicleWidth] = useState(-1);
  const [left, setLeft] = useState(-1);

  useEffect(() => {
    let dimensions_list = props?.dimensionsList;
    dimensions_list?.forEach((obj) => {
      if (obj.property_name === VEHICLE_DIMENSIONS_KEY_ENUM.VEHICLE_HEIGHT) {
        setVehicleHeight(obj.value);
      }
      if (obj.property_name === VEHICLE_DIMENSIONS_KEY_ENUM.VEHICLE_LENGTH) {
        setVehicleLength(obj.value);
      }
      if (obj.property_name === VEHICLE_DIMENSIONS_KEY_ENUM.VEHICLE_WIDTH) {
        setVehicleWidth(obj.value);
      }
      if (obj.property_name.trim() === VEHICLE_DIMENSIONS_KEY_ENUM.NX_FRONT_OF_VEHICLE_X) {
        setLeft(obj.value);
      }
    });
  }, [props]);

  const [x0, setX0] = useState(-1);
  const [y0, setY0] = useState(-1);
  const [x1, setX1] = useState(-1);
  const [y1, setY1] = useState(-1);

  useEffect(() => {
    setX0(left);
    setY0(vehicle_width / 2);
    setX1(vehicle_length + left);
    setY1(-vehicle_width / 2);
  }, [left, vehicle_width, vehicle_length, vehicle_height]);

  const [globalMin, setGlobalMin] = useState(null);
  const [globalMax, setGlobalMax] = useState(null);

  const getPlotRange = (absoluteMin, absoluteMax) => {
    if (globalMin == null || globalMin > absoluteMin) {
      setGlobalMin(absoluteMin);
    }
    if (globalMax == null || globalMax < absoluteMax) {
      setGlobalMax(absoluteMax);
    }
  };

  const processSensorData = (sensorData) => {
    const x = [];
    const y = [];
    const a = [];
    let absoluteMax = null;

    for (let sensorLocation in sensorData) {
      const data = sensorData[sensorLocation];
      const _x = data?.origin_point_x;
      const _y = data?.origin_point_y;
      const _a = data?.yaw;
      if (_x != null && _y != null) {
        x.push(_x);
        y.push(_y);
        a.push(_a);
      }
    }

    if (x.length > 0 && y.length > 0) {
      absoluteMax = getMaxFinite([...x, ...y]);
    }

    return {x, y, a, plotRange: absoluteMax ? [-absoluteMax + 1000, absoluteMax + 1000] : null};
  };

  const initSensorData = (sensorData, setX, setY, setA) => {
    if (sensorData) {
      const {x, y, a} = processSensorData(sensorData);
      if (setX != null)
        setX(x);
      if (setY != null)
        setY(y);
      if (setA != null)
        setA(a);
    } else {
      if (setX != null)
        setX([]);
      if (setY != null)
        setY([]);
      if (setA != null)
        setA([]);
    }
  };

  useEffect(() => {
    const sensorsDataMap = props.plotlyTraces;
    initSensorData(sensorsDataMap?.['Radar'], setRadarX, setRadarY, setRadarA);
    initSensorData(sensorsDataMap?.['Camera'], setCameraX, setCameraY, setCameraA);
    initSensorData(sensorsDataMap?.['IMU'], setIMUX, setIMUY, setIMUA);
    initSensorData(sensorsDataMap?.['Antenna'], setAntennaX, setAntennaY, null); // Pass null for setA
    initSensorData(sensorsDataMap?.['Lidar'], setLidarX, setLidarY, setLidarA);
    initSensorData(sensorsDataMap?.['Ultrasonic'], setUltrasonicsX, setUltrasonicsY, setUltrasonicsA);
  }, [props]);

  useEffect(() => {
    const allX = [...radarX, ...camerax, ...imux, ...antennax, ...lidarX, ...ultrasonicsX];
    const allY = [...radarY, ...cameray, ...imuy, ...antennay, ...lidarY, ...ultrasonicsY];

    if (allX.length > 0 && allY.length > 0) {
      const absoluteMax = getMaxFinite([...allX, ...allY]);
      const absoluteMin = getMinFinite([...allX, ...allY]);
      const abs = Math.max(Math.abs(absoluteMax), Math.abs(absoluteMin));
      getPlotRange(-abs - 1000, abs + 1000);
    }
  }, [radarX, camerax, imux, antennax, lidarX, ultrasonicsX, radarY, cameray, imuy, antennay, lidarY, ultrasonicsY]);

  const calculateCombinedData = (x, y, a, combinedX, combinedY, markerSize, length) => {
    x?.forEach((val, i) => {
      const dx = val + Math.cos((a[i] * Math.PI) / 180) * length;
      const dy = y[i] + Math.sin((a[i] * Math.PI) / 180) * length;
      combinedX.push(val, dx, null);
      combinedY.push(y[i], dy, null);
      markerSize.push(10, 2, null);
    });
  };

  const initCombinedData = (x, y, a, setCombinedX, setCombinedY, setMarkerSize) => {
    const _combinedX = [];
    const _combinedY = [];
    const _markerSize = [];
    calculateCombinedData(x, y, a, _combinedX, _combinedY, _markerSize, length);
    setCombinedX(_combinedX);
    setCombinedY(_combinedY);
    setMarkerSize(_markerSize);
  };

  const [radarcombinedX, setRadarCombinedX] = useState([]);
  const [radarcombinedY, setRadarCombinedY] = useState([]);
  const [radarMarkerSize, setCombinedRadarMarkerSize] = useState([]);
  useEffect(() => {
    initCombinedData(radarX, radarY, radarA, setRadarCombinedX, setRadarCombinedY, setCombinedRadarMarkerSize);
  }, [radarX, radarY, radarA]);

  const [cameracombinedX, setCameraCombinedX] = useState([]);
  const [cameracombinedY, setCameraCombinedY] = useState([]);
  const [cameramarkersize, setCameraMarkerSize] = useState([]);
  useEffect(() => {
    initCombinedData(camerax, cameray, cameraa, setCameraCombinedX, setCameraCombinedY, setCameraMarkerSize);
  }, [camerax, cameray, cameraa]);

  const [imucombinedX, setIMUCombinedX] = useState([]);
  const [imucombinedY, setIMUCombinedY] = useState([]);
  const [imumarkersize, setIMUMarkerSize] = useState([]);
  useEffect(() => {
    initCombinedData(imux, imuy, imua, setIMUCombinedX, setIMUCombinedY, setIMUMarkerSize);
  }, [imux, imuy, imua]);

  const [lidarcombinedX, setLidarCombinedX] = useState([]);
  const [lidarcombinedY, setLidarCombinedY] = useState([]);
  const [lidarmarkersize, setLidarMarkerSize] = useState([]);
  useEffect(() => {
    initCombinedData(lidarX, lidarY, lidarA, setLidarCombinedX, setLidarCombinedY, setLidarMarkerSize);
  }, [lidarX, lidarY, lidarA]);

  const [ultrasonicscombinedX, setUltrasonicsCombinedX] = useState([]);
  const [ultrasonicscombinedY, setUltrasonicsCombinedY] = useState([]);
  const [ultrasonicsmarkersize, setUltrasonicsMarkerSize] = useState([]);
  useEffect(() => {
    initCombinedData(ultrasonicsX, ultrasonicsY, ultrasonicsA, setUltrasonicsCombinedX, setUltrasonicsCombinedY, setUltrasonicsMarkerSize);
  }, [ultrasonicsX, ultrasonicsY, ultrasonicsA]);

  const createTrace = (combinedX, combinedY, markerSize, color, name, symbol = 'circle') => ({
    x: combinedX,
    y: combinedY,
    mode: 'lines+markers',
    type: 'scatter',
    marker: {size: markerSize, symbol: symbol},
    line: {shape: 'linear', color: color},
    name: name,
    showlegend: true
  });

  const [radar, setRadar] = useState({});
  useEffect(() => {
    setRadar(createTrace(radarcombinedX, radarcombinedY, radarMarkerSize, 'rgb(245,177,29)', 'Radar', 'square'));
  }, [radarcombinedX, radarcombinedY, radarMarkerSize]);

  const [camera, setCamera] = useState({});
  useEffect(() => {
    setCamera(createTrace(cameracombinedX, cameracombinedY, cameramarkersize, 'rgb(111,8,138)', 'Camera'));
  }, [cameracombinedX, cameracombinedY, cameramarkersize]);

  const [imu, setIMU] = useState({});
  useEffect(() => {
    setIMU(createTrace(imucombinedX, imucombinedY, imumarkersize, 'green', 'IMU', 'triangle'));
  }, [imucombinedX, imucombinedY, imumarkersize]);

  const [lidar, setLidar] = useState({});
  useEffect(() => {
    setLidar(createTrace(lidarcombinedX, lidarcombinedY, lidarmarkersize, 'red', 'Lidar'));
  }, [lidarcombinedX, lidarcombinedY, lidarmarkersize]);

  const [ultrasonics, setUltrasonics] = useState({});
  useEffect(() => {
    setUltrasonics(createTrace(ultrasonicscombinedX, ultrasonicscombinedY, ultrasonicsmarkersize, 'brown', 'Ultrasonics'));
  }, [ultrasonicscombinedX, ultrasonicscombinedY, ultrasonicsmarkersize]);

  const [antenna, setAntenna] = useState([]);
  useEffect(() => {
    setAntenna({
      x: antennax,
      y: antennay,
      mode: 'markers',
      type: 'scatter',
      marker: {size: 8},
      line: {shape: 'linear', color: 'blue'},
      name: 'Antenna',
      showlegend: true
    });
  }, [antennax, antennay]);

  const [data, setData] = useState([]);
  useEffect(() => {
    setData([radar, camera, lidar, ultrasonics, imu, antenna]);
  }, [radar, camera, imu, lidar, ultrasonics, antenna]);

  const useForceUpdate = () => {
    const [value, setValue] = useState(0);
    return () => setValue(value => value + 1);
  };
  const forceUpdate = useForceUpdate();

  const handleClick = () => {
    forceUpdate();
  };

  const config = {
    modeBarButtonsToAdd: [
      {
        name: 'Square Plot',
        icon: {
          width: 80,
          height: 80,
          path: 'M78,22V79H21V22H78m9-9H12V88H87V13ZM68,46.22H31V54H68ZM53,32H45.22V69H53Z',
          transform: 'matrix(1 0 0 1 -10 -10)'
        },
        click: () => handleClick(),
      },
    ]
  };
  const confref = useRef(null);
  confref.current = config;

  const {adasProgramId, vehicleProgramId} = props;
  return <Plot
    // key={adasProgramId ? adasProgramId : vehicleProgramId}
    modeBarButtonsToAdd={config.modeBarButtonsToAdd}
    config={confref.current}
    useResizeHandler={true}
    style={{height: 800, width: 800, paddingBottom: "3px"}}
    data={data}
    layout={{
      title: 'Top View (Yaw Displayed by Lines)',
      shapes: [
        {
          type: 'rect',
          xref: 'x',
          yref: 'y',
          x0: x0,
          y0: y0,
          x1: x1,
          y1: y1,
          line: {
            color: 'black',
            width: 3
          },
          fillcolor: 'rgba(128, 128, 128, 0.3)'
        }
      ],
      legend: {
        tracegroupgap: 100,

        // Horizontal above graph
        orientation: 'h',
        y: 1.05

        // In top left of graph
        // y: 1,
        // x: 0,
        // xanchor: 'left'
      },
      xaxis: {
        title: 'X Axis (mm)',
        // rangemode: "tozero",
        nticks: 8,
        range: [globalMin, globalMax]
      },
      yaxis: {
        title: 'Y Axis (mm)',
        // rangemode: "tozero",
        nticks: 8,
        range: [globalMin, globalMax]
      },
    }}
  />;
};

export default Plotly2DTracesTop;